import type {ReactElement} from 'react';
import React, {useEffect, useMemo, useRef} from 'react';
import type {AnimationLightboxThumbnailStripProps} from './animation-lightbox-thumbnail-strip.types';
import styles from './animation-lightbox-thumbnail-strip.module.scss';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {AiChatAnimation} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations.types';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';

const THUMB_HEIGHT_PX = 64;
const GAP_PX = 8;
const HORIZONTAL_PADDING_PX = 16;
const VISIBLE_THUMB_COUNT = 5.2;

export function AnimationLightboxThumbnailStrip({animations, currentIndex, onThumbnailClick, isCreating}: AnimationLightboxThumbnailStripProps): ReactElement {
  const selectedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'center'});
  }, [currentIndex]);

  const firstAnimation = animations[0];
  const stripAspectRatio = firstAnimation?.width && firstAnimation?.height ? firstAnimation.width / firstAnimation.height : 1;
  const thumbWidth = THUMB_HEIGHT_PX * stripAspectRatio;
  const stripMaxWidth = VISIBLE_THUMB_COUNT * thumbWidth + (VISIBLE_THUMB_COUNT - 0.2) * GAP_PX + 2 * HORIZONTAL_PADDING_PX;
  const thumbStyle = useMemo(() => ({'--thumb-aspect-ratio': String(stripAspectRatio), width: `${thumbWidth}px`}) as React.CSSProperties, [stripAspectRatio, thumbWidth]);
  const stripStyle = useMemo((): React.CSSProperties => ({maxWidth: `${stripMaxWidth}px`}), [stripMaxWidth]);

  const getCreatingThumbnail = (): ReactElement => {
    const isSelected = Boolean(isCreating) && currentIndex === 0;

    return (
      <ClickableDiv
        key="creating"
        ref={isSelected ? selectedRef : undefined}
        className={`${styles.thumbnailContainer} ${isSelected ? styles.thumbnailContainerSelected : ''}`}
        style={thumbStyle}
        onClick={(): void => {
          onThumbnailClick(0);
        }}
      >
        <SkeletonBrick className={styles.thumbnailImage} theme={SkeletonBrickTheme.AI_ROTATING_GRADIENT} />
      </ClickableDiv>
    );
  };

  const getPlayIcon = (isSelected: boolean): ReactElement | null => {
    if (isSelected) {
      return null;
    }

    return (
      <div className={styles.playIcon}>
        <Icon icon={'icon-play-video'} size={IconSize.SIZE_ICON_16} shape={IconShape.CIRCLE} type={IconType.SHADOW} />
      </div>
    );
  };

  const getThumbnail = (animation: AiChatAnimation, index: number): ReactElement => {
    const offsetIndex = isCreating ? index + 1 : index;
    const isSelected = offsetIndex === currentIndex;

    return (
      <ClickableDiv
        key={animation.animationHashedId}
        ref={isSelected ? selectedRef : undefined}
        className={`${styles.thumbnailContainer} ${isSelected ? styles.thumbnailContainerSelected : ''}`}
        style={thumbStyle}
        onClick={(): void => {
          onThumbnailClick(offsetIndex);
        }}
      >
        <img src={animation.thumbnailUrl} className={styles.thumbnailImage} alt="" />
        <div className={`${styles.shadow} ${isSelected ? styles.shadowSelected : ''}`} />
        {getPlayIcon(isSelected)}
      </ClickableDiv>
    );
  };

  return (
    <div className={styles.strip} style={stripStyle}>
      {isCreating ? getCreatingThumbnail() : null}
      {animations.map((animation, index) => getThumbnail(animation, index))}
    </div>
  );
}
